import type { DescriptorFieldOption } from './types';

/**
 * Resolve one proposed value against a closed option set — the SINGLE rule for
 * "which of the user's options did the agent mean?" (AID-393).
 *
 * Shared deliberately. `coerceFieldValue` uses it to decide what Apply writes,
 * and `formatProposalValue` uses it to decide what the card shows. If the two
 * drifted, the card could label a value that Apply then rejects — showing the
 * user an option they never actually get.
 *
 * Matching is EXACT, in two passes:
 *
 *  1. the wire value (`"2"` or `2` → the option whose `String(value)` is `"2"`),
 *     which is what a model reading the allowed-values list sends; then
 *  2. the LABEL, case-insensitively and trimmed (`"Medium"` → the option worth
 *     `2`), because the user reasons in labels and so does the model.
 *
 * (2) is not fuzzy matching — it is an exact match against a legal label. An
 * ambiguous label (two options sharing one) matches NOTHING rather than picking
 * the first: silently choosing between two of the user's own options is worse
 * than declining. Anything else — including a value that merely looks plausible
 * — is a miss, and the caller reports it rather than writing it.
 *
 * Returning the OPTION rather than its value is what preserves the field's type:
 * the caller writes `option.value`, so a numeric taxonomy stays numeric and a
 * string enum stays a string with no conversion anywhere.
 */
export const matchDescriptorOption = (
  options: DescriptorFieldOption[],
  raw: unknown
): DescriptorFieldOption | undefined => {
  if (
    typeof raw !== 'string' &&
    typeof raw !== 'number' &&
    typeof raw !== 'boolean'
  ) {
    return undefined;
  }

  const candidate = String(raw).trim();
  if (candidate === '') {
    return undefined;
  }

  const byValue = options.find((option) => String(option.value) === candidate);
  if (byValue) {
    return byValue;
  }

  const folded = candidate.toLocaleLowerCase();
  const byLabel = options.filter(
    (option) => option.label.trim().toLocaleLowerCase() === folded
  );

  return byLabel.length === 1 ? byLabel[0] : undefined;
};
